import BriteboundLogoHorizDark from '@/assets/britebound/logo-britebound_horiz-dark.svg';
import BriteboundLogoHorizLight from '@/assets/britebound/logo-britebound_horiz-light.svg';
import JFTFLogo from '@/assets/logos/jobs-for-the-future-logo.png';
import JFTFLogoLight from '@/assets/logos/jobs-for-the-future-logo-light.svg';
import KuderLogo from '@/assets/logos/kuder-logo.svg';
import KuderLogoLight from '@/assets/logos/kuder-logo-light.svg';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { H2, P } from '@/components/ui/typography';
import { renderTagged } from '@/lib/renderTagged';
import { cn } from '@/lib/utils';
import { FooterMenuDropdown } from './FooterDropdown';
import { CONTACT_LINK, footerTracking } from './footerNavigation';
import type { FooterStrings } from './footerStrings';

interface LogoProps {
    /** Link targets for this page's language (see FooterBodyProps.hrefs). */
    href: (path: string) => string;
    reloadLinks?: boolean;
    shouldUseDarkLogo: boolean;
    strings: FooterStrings;
    loggedIn: boolean;
}

const BriteboundLogo = ({ shouldUseDarkLogo, strings, href, reloadLinks, loggedIn }: LogoProps) => {
    const logoHorizontal = shouldUseDarkLogo ? BriteboundLogoHorizDark : BriteboundLogoHorizLight;

    return (
        <div className="flex w-full min-w-0 max-w-[446px] flex-col">
            <Link
                to={href('/partners-and-vendors')}
                reloadDocument={reloadLinks}
                title={strings.partnersLinkTitle}
                className="block"
                {...footerTracking('Footer Britebound Logo Clicked', 'footer-britebound-logo', loggedIn)}
            >
                <img
                    src={logoHorizontal}
                    alt={strings.logos.britebound}
                    className="h-auto w-full object-contain"
                />
            </Link>
        </div>
    );
};

const PartnershipLogos = ({ shouldUseDarkLogo, strings, href, reloadLinks, loggedIn }: LogoProps) => {
    const jftfLogo = shouldUseDarkLogo ? JFTFLogo : JFTFLogoLight;
    const kuderLogo = shouldUseDarkLogo ? KuderLogo : KuderLogoLight;

    return (
        <div className="flex w-full flex-col lg:w-auto lg:shrink-0 lg:items-end">
            <Link
                to={href('/partners-and-vendors')}
                reloadDocument={reloadLinks}
                title={strings.partnersLinkTitle}
                className="flex w-full max-w-[365px] flex-col items-start gap-2.5 lg:w-auto lg:max-w-none"
                {...footerTracking('Footer Partnership Logos Clicked', 'footer-partnership-logos', loggedIn)}
            >
                <H2
                    variant="type-body-default"
                    className="light:text-gz-neutral-800 dark:text-gz-neutral-100"
                >
                    {strings.partnership}
                </H2>
                <span className="flex w-full items-center justify-between lg:w-auto lg:justify-start lg:gap-[46px]">
                    <img
                        src={jftfLogo}
                        alt={strings.logos.jff}
                        className="h-auto w-[39%] object-contain lg:w-[175px]"
                    />
                    <img
                        src={kuderLogo}
                        alt={strings.logos.kuder}
                        className="h-auto w-[50.7%] object-contain lg:w-[227px]"
                    />
                </span>
            </Link>
        </div>
    );
};

const FooterNav = ({ languageSwitcher, strings, hrefs, reloadLinks, loggedIn }: { languageSwitcher: ReactNode; strings: FooterStrings; hrefs?: Record<string, string>; reloadLinks?: boolean; loggedIn: boolean }) => {
    return (
        <nav aria-label={strings.navLabel}>
            <ul
                className={cn(
                    'mx-auto grid w-max max-w-full grid-cols-[max-content_max-content] items-center justify-items-center gap-x-8 gap-y-4',
                    'md:mx-0 md:flex md:w-auto md:max-w-none md:flex-wrap md:justify-center md:gap-[42px]',
                )}
            >
                <li>
                    <FooterMenuDropdown menu="resources" labels={strings.menus.resources} hrefs={hrefs} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                </li>
                <li>
                    <FooterMenuDropdown menu="legal" labels={strings.menus.legal} hrefs={hrefs} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                </li>
                <li>
                    <Link
                        to={hrefs?.[CONTACT_LINK.to] ?? CONTACT_LINK.to}
                        reloadDocument={reloadLinks}
                        {...footerTracking('Footer Contact Clicked', 'footer-contact', loggedIn)}
                        className={cn(
                            'block rounded-[47px] px-4 py-1 text-[18px] leading-6 font-bold underline',
                            'light:text-gz-neutral-900 dark:text-gz-neutral-100',
                            'light:hover:bg-gz-tan-350 dark:hover:bg-gz-blue-700',
                            'focus-visible:outline-2 focus-visible:outline-offset-2',
                            'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-gz-marine-500',
                        )}
                    >
                        {strings.contact}
                    </Link>
                </li>
                {/*
                  * Spanish rollout phase 0.5 — see docs/plans/spanish-translation/.
                  * In the footer deliberately: the switcher is not a promoted feature yet, because
                  * only ZigBot replies and landing occupations respond to it. Move it somewhere
                  * prominent once interface copy and content are actually translated.
                  */}
                {languageSwitcher && <li>{languageSwitcher}</li>}
            </ul>
        </nav>
    );
};

const ExternalLink = ({ href, children, click, buttonName, loggedIn }: { href: string; children: ReactNode; click: string; buttonName: string; loggedIn: boolean }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
        {...footerTracking(click, buttonName, loggedIn)}
    >
        {children}
    </a>
);

export interface FooterBodyProps {
    /** Dark logo variants: only a signed-in user on the dark theme gets them. */
    darkLogos: boolean;
    /** The SPA's LanguageSwitcher, or a server render's locale links. */
    languageSwitcher?: ReactNode;
    /** Translated by the caller (see footerStrings.ts). */
    strings: FooterStrings;
    /**
     * Link targets for the page's language, by default path: a server-rendered Spanish page passes
     * `{ '/faqs': '/es/faqs', ... }` so its links stay in Spanish. Absent (the SPA): the paths as they are.
     */
    hrefs?: Record<string, string>;
    /**
     * Full page loads for its internal links instead of in-app routing: the SPA sets this for a signed-out reader
     * with a non-English choice (`shouldReloadForLocale`), so the server can send them to the page in that language.
     */
    reloadLinks?: boolean;
    /** Signed in at render time. Public server pages are always signed out. */
    loggedIn: boolean;
}

/**
 * The footer without hooks: everything that depends on the viewer comes in as props, so the SPA
 * (Footer) and server components render the same markup.
 */
export const FooterBody = ({ darkLogos, languageSwitcher, strings, hrefs, reloadLinks, loggedIn }: FooterBodyProps) => {
    const href = (path: string) => hrefs?.[path] ?? path;
    return (
        <footer
            className={cn(
                'light:bg-gz-tan-200 dark:bg-gz-blue-900',
                'shadow-[0_-2px_4px_0_rgba(10,21,78,0.04)]',
            )}
        >
            <div className="container mx-auto flex w-full min-w-0 flex-col gap-8 px-6 py-12">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                    <BriteboundLogo shouldUseDarkLogo={darkLogos} strings={strings} href={href} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                    <PartnershipLogos shouldUseDarkLogo={darkLogos} strings={strings} href={href} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                </div>
                <FooterNav languageSwitcher={languageSwitcher} strings={strings} hrefs={hrefs} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                <P
                    variant="type-caption"
                    className={cn(
                        'mx-auto max-w-[736px] text-center text-[14px] leading-5 tracking-[0.07px] break-words',
                        'light:text-gz-neutral-800 dark:text-gz-neutral-100',
                    )}
                >
                    {strings.disclaimer}
                    {' '}
                    {renderTagged(strings.twemojiCredit, {
                        1: children => (
                            <ExternalLink href="https://github.com/twitter/twemoji" click="Footer Twemoji Clicked" buttonName="footer-twemoji" loggedIn={loggedIn}>
                                {children}
                            </ExternalLink>
                        ),
                        2: children => (
                            <ExternalLink href="https://creativecommons.org/licenses/by/4.0/" click="Footer Creative Commons Clicked" buttonName="footer-creative-commons" loggedIn={loggedIn}>
                                {children}
                            </ExternalLink>
                        ),
                    })}
                </P>
            </div>
        </footer>
    );
};
